import { useEffect, useRef, useState } from "react";
import { faPlus, faSplit } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { useClickOutside, useStableCallback } from "common/hooks";
import { IPatient } from "common/types";
import { dedupeByKey } from "common/utils/entities";
import { Optional } from "common/utils/types";
import Angle from "common/components/Angle";
import Icon from "common/components/Icon";
import Image from "common/components/Image";
import LinkButton from "common/components/LinkButton";

import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import TextInput from "client/common/components/inputs/TextInput";
import LoadingContainer from "client/common/components/loading/LoadingContainer";

import PatientMultiSelectPanel, { PatientForMultiSelectPanel } from "./PatientMultiSelectPanel";
import PatientSelectPanel, { MultiPatient, Option } from "./PatientSelectPanel";
import { useAccountCreatePetDrawer } from "../stackables";
import { usePatientPickerList } from "../hooks";
import { imageForSpecies } from "../utils";

import styles from "../styles/PatientSelect.module.less";

const noop = () => null;

const MULTI_PATIENT_OPTION: MultiPatient = { multi: true };

/** Fits ~6 rows, with the 7th cut off to hint at scrolling */
const POPOVER_MAX_HEIGHT = 420;
/** Space kept between the popover and the top of a software keyboard, plus scroll-margin */
const POPOVER_KEYBOARD_MARGIN = 32;

type PatientSelectOption = Pick<IPatient, "id" | "name_display" | "species" | "species_display"> & {
  is_active?: boolean;
};

type PatientAvatarProps = {
  patient: PatientSelectOption;
  isActive?: boolean;
};

const PatientAvatar = ({ patient, isActive = false }: PatientAvatarProps) => (
  <div className={styles.avatar}>
    <Image
      className={styles.avatarImage}
      src={imageForSpecies(patient.species, isActive)}
      alt={patient.species_display}
    />
  </div>
);

const PatientPickerLoadState = ({
  picker,
}: {
  picker: ReturnType<typeof usePatientPickerList>;
}) => {
  if (picker.isLoading) {
    return <LoadingContainer />;
  }
  if (picker.error && picker.results.length === 0) {
    return <FetchErrorAlert error={picker.error} />;
  }
  return null;
};

const isInactive = (patient: PatientSelectOption) => patient.is_active === false;

type PatientSearchDropdownProps = {
  picker: ReturnType<typeof usePatientPickerList>;
  label: string;
  selected: PatientSelectOption[];
  recentPatients: PatientSelectOption[];
  closeOnSelect: boolean;
  onSelect: (patient: PatientSelectOption) => void;
  hasMulti: boolean;
  onSelectMulti: () => void;
  onAdd: () => void;
  renderTag: (option: Option) => React.ReactNode;
  showSelectedOverlay?: boolean;
};

/** Searchable dropdown for households too large to show every pet as a chip */
const PatientSearchDropdown = ({
  picker,
  label,
  selected,
  recentPatients,
  closeOnSelect,
  onSelect,
  hasMulti,
  onSelectMulti,
  onAdd,
  renderTag,
  showSelectedOverlay = true,
}: PatientSearchDropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popoverMaxHeight, setPopoverMaxHeight] = useState(POPOVER_MAX_HEIGHT);
  const { qInput, onQChange, results, unfilteredCount, isSearching } = picker;
  const selectedIds = new Set(selected.map((patient) => patient.id));
  const overlayPatient = selected[0];

  const close = useStableCallback(() => {
    setIsOpen(false);
    onQChange("");
    // popover preventDefault keeps the input focused so the iOS keyboard stays
    // up while tapping options; blur so the next tap is a real focus event
    inputRef.current?.blur();
  });

  useClickOutside(containerRef, close);

  // Software keyboards (iOS) need two fixes that desktop never hits:
  // - focusing scrolls the input to the middle of the shrunken viewport, hiding most of the
  //   popover, so once the keyboard has resized the visual viewport pull the input back to
  //   the top and fit the popover into the space left above the keyboard
  // - the keyboard's "Done" only blurs the input, so treat "keyboard dismissed while the
  //   input is unfocused" as closing the picker
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!isOpen || !viewport) {
      return undefined;
    }
    let wasKeyboardOpen = false;
    const onViewportResize = () => {
      const container = containerRef.current;
      const isKeyboardOpen = viewport.height < window.innerHeight;
      if (!container) {
        return;
      }
      if (isKeyboardOpen) {
        container.scrollIntoView({ block: "start", behavior: "smooth" });
        setPopoverMaxHeight(
          Math.min(
            POPOVER_MAX_HEIGHT,
            viewport.height - container.offsetHeight - POPOVER_KEYBOARD_MARGIN,
          ),
        );
      } else if (wasKeyboardOpen && !container.contains(document.activeElement)) {
        close();
      } else {
        setPopoverMaxHeight(POPOVER_MAX_HEIGHT);
      }
      wasKeyboardOpen = isKeyboardOpen;
    };
    viewport.addEventListener("resize", onViewportResize);
    return () => {
      viewport.removeEventListener("resize", onViewportResize);
      setPopoverMaxHeight(POPOVER_MAX_HEIGHT);
    };
  }, [isOpen, close]);

  const handleSelect = (patient: PatientSelectOption) => {
    onSelect(patient);
    if (closeOnSelect) {
      close();
    }
  };

  const handleAction = (action: () => void) => {
    action();
    close();
  };

  const hasQuery = qInput.trim() !== "";
  const recent = hasQuery ? [] : dedupeByKey(recentPatients, "id");
  const recentIds = new Set(recent.map((patient) => patient.id));
  const remaining = results.filter((patient) => !recentIds.has(patient.id));
  // keep selected pets visible at the top of the unfiltered list even when they fall
  // outside the first page
  const pinned = selected.filter(
    (patient) =>
      !hasQuery &&
      !recentIds.has(patient.id) &&
      !remaining.some((result) => result.id === patient.id),
  );
  const options = [...pinned, ...remaining];

  const renderOption = (patient: PatientSelectOption) => {
    const isActive = selectedIds.has(patient.id);
    return (
      <LinkButton
        key={patient.id}
        className={cx(styles.option, isActive && styles.active)}
        onClick={() => handleSelect(patient)}
      >
        <PatientAvatar patient={patient} isActive={isActive} />
        <span className={styles.optionName}>{patient.name_display}</span>
        <span className={styles.optionExtra}>
          {renderTag(patient as IPatient)}
          {isInactive(patient) && <span className={styles.muted}>Inactive</span>}
        </span>
      </LinkButton>
    );
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {/* the input stays mounted so opening doesn't replay its label animation; the
          selected pet is layered over it and lets clicks through to focus the input */}
      <div className={styles.search} onFocusCapture={() => setIsOpen(true)}>
        <TextInput
          ref={inputRef}
          label={label}
          value={qInput}
          onChange={onQChange}
          status={isSearching ? "loading" : "idle"}
        />
        {showSelectedOverlay && overlayPatient && !isOpen && (
          <div className={styles.selected}>
            <PatientAvatar patient={overlayPatient} isActive={true} />
            <span className={styles.selectedName}>
              {overlayPatient.name_display}
              {selected.length > 1 && (
                <span className={styles.muted}> +{selected.length - 1} more</span>
              )}
              {isInactive(overlayPatient) && <span className={styles.muted}> · Inactive</span>}
            </span>
          </div>
        )}
        {/* the input's loading spinner takes the chevron's spot while a search is pending */}
        {!isSearching && <Angle direction="down" className={styles.angle} isActive={isOpen} />}
      </div>
      <div
        className={cx(styles.popover, !isOpen && styles.hidden)}
        style={{ maxHeight: popoverMaxHeight }}
        // keep focus (and the software keyboard) on the input while interacting with the list;
        // click events still fire on the options
        onPointerDown={(event) => event.preventDefault()}
      >
        {recent.length > 0 && (
          <>
            <div className={styles.groupLabel}>Recent pets</div>
            {recent.map(renderOption)}
          </>
        )}
        {!hasQuery && <div className={styles.groupLabel}>Search all {unfilteredCount} pets</div>}
        {options.map(renderOption)}
        {!isSearching && recent.length === 0 && options.length === 0 && (
          <div className={styles.empty}>No pets found</div>
        )}
        <div className={styles.footer}>
          {hasMulti && (
            <LinkButton className={styles.footerAction} onClick={() => handleAction(onSelectMulti)}>
              <span className={styles.footerIcon}>
                <Icon icon={faSplit} />
              </span>
              Split between pets
            </LinkButton>
          )}
          <LinkButton className={styles.footerAction} onClick={() => handleAction(onAdd)}>
            <span className={styles.footerIcon}>
              <Icon icon={faPlus} />
            </span>
            Add new pet
          </LinkButton>
        </div>
      </div>
    </div>
  );
};

type PatientSelectProps = {
  value: Optional<IPatient>;
  onSelect: (patient: IPatient) => void;
  /** Pets surfaced in a "Recent pets" group above the full list of a large household */
  recentPatients?: IPatient[];
  /** Offer "Split between pets"; only shown when the household has more than one pet */
  canSplit?: boolean;
  onSelectMulti?: () => void;
  onAdd: () => void;
  renderTag?: (option: Option) => React.ReactNode;
};

/**
 * Picks a single pet from the household. Small households show every pet as a chip;
 * large households get a searchable dropdown backed by the paginated patient list.
 */
const PatientSelect = ({
  value,
  onSelect,
  recentPatients = [],
  canSplit = false,
  onSelectMulti = noop,
  onAdd,
  renderTag = noop,
}: PatientSelectProps) => {
  const picker = usePatientPickerList();
  const hasMulti = canSplit && picker.unfilteredCount > 1;
  const selected = value ? [value] : [];
  if (picker.isLoading || (picker.error && picker.results.length === 0)) {
    return <PatientPickerLoadState picker={picker} />;
  }

  if (picker.isLargeHousehold) {
    return (
      <PatientSearchDropdown
        picker={picker}
        label="Select pet"
        selected={selected}
        recentPatients={recentPatients}
        closeOnSelect={true}
        onSelect={(patient) => {
          const match =
            (value && value.id === patient.id ? value : undefined) ??
            recentPatients.find((recent) => recent.id === patient.id) ??
            picker.results.find((result) => result.id === patient.id);
          if (match) {
            onSelect(match);
          }
        }}
        hasMulti={hasMulti}
        onSelectMulti={onSelectMulti}
        onAdd={onAdd}
        renderTag={renderTag}
      />
    );
  }

  return (
    <PatientSelectPanel
      options={[...picker.results, ...(hasMulti ? [MULTI_PATIENT_OPTION] : [])]}
      splitPatients={picker.results}
      value={value}
      onSelect={onSelect}
      onSelectMulti={onSelectMulti}
      hasAdd={true}
      onAdd={onAdd}
      renderTag={renderTag}
    />
  );
};

type PatientMultiSelectProps = {
  values: PatientForMultiSelectPanel[];
  onChange: (values: PatientForMultiSelectPanel[]) => void;
};

/**
 * Picks one or more pets. Small households show every pet as a chip; large households
 * search from a dropdown and render the selected set as chips below.
 */
export const PatientMultiSelect = ({ values, onChange }: PatientMultiSelectProps) => {
  const picker = usePatientPickerList();
  const { open: openCreatePet } = useAccountCreatePetDrawer();

  const handleSelect = (patient: PatientSelectOption) => {
    if (values.some((value) => value.id === patient.id)) {
      onChange(values.filter((value) => value.id !== patient.id));
      return;
    }
    onChange([...values, patient]);
  };

  if (picker.isLoading || (picker.error && picker.results.length === 0)) {
    return <PatientPickerLoadState picker={picker} />;
  }

  if (picker.isLargeHousehold) {
    return (
      <div className={styles.multi}>
        <PatientSearchDropdown
          picker={picker}
          label="Add a pet"
          selected={values}
          recentPatients={[]}
          closeOnSelect={true}
          showSelectedOverlay={false}
          onSelect={handleSelect}
          hasMulti={false}
          onSelectMulti={noop}
          onAdd={() =>
            openCreatePet({
              onSuccess: (patient) => onChange([...values, patient]),
            })
          }
          renderTag={noop}
        />
        {values.length > 0 && (
          <>
            <div className={styles.selectedHeader}>
              <span className={styles.selectedCount}>Selected · {values.length}</span>
              <LinkButton className={styles.clearAll} onClick={() => onChange([])}>
                Clear all
              </LinkButton>
            </div>
            <PatientMultiSelectPanel
              values={values}
              onChange={onChange}
              options={values}
              hasAdd={false}
            />
          </>
        )}
      </div>
    );
  }

  return <PatientMultiSelectPanel values={values} onChange={onChange} options={picker.results} />;
};

export default PatientSelect;
